/**
 * Address-book hooks — backed by the Vault CRM (the contact-book arche), so
 * contacts you manage from email are the SAME personal contacts that appear in
 * Contacts everywhere else, and vice-versa.
 *
 * The curated CRM is the primary book. Alongside it, `vault_email_contacts` is a
 * per-account cache auto-populated from the recipients of your Sent mail
 * (`useHarvestedEmailContacts`) — these power compose autocomplete and the
 * address book's "From your mail" suggestions, and can be promoted into the CRM.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ContactDetail, type MailboxContactDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const cb = queryKeys.contactBook;
const mb = queryKeys.email.mailbox;

/**
 * Harvested "from your mail" contacts — the cache auto-populated from the
 * recipients of your Sent mail (see the sync-time harvest). Distinct from the
 * curated CRM: these are suggestions, promotable to real contacts. Omit
 * `accountId` to aggregate across ALL accounts (deduped by email).
 */
export function useHarvestedEmailContacts(
  accountId: string | undefined,
  enabled: boolean,
  search = '',
) {
  const term = search.trim();
  return useQuery<MailboxContactDto[]>({
    queryKey: mb.contactList(accountId ?? 'all', term),
    queryFn: async () =>
      (await api.listEmailMailboxContacts(accountId, term || undefined)).contacts,
    enabled,
  });
}

/** Primary email for a contact (first listed, or '' when none). */
export function contactPrimaryEmail(c: ContactDetail): string {
  return c.emails[0]?.value ?? '';
}

/** Display label — full name, falling back to the primary email. */
export function contactDisplayName(c: ContactDetail): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  return name || contactPrimaryEmail(c) || '(no name)';
}

export function useAddressBook(enabled: boolean, search = '') {
  const filters = { search: search.trim() || undefined, status: 'active' as const };
  return useQuery<ContactDetail[]>({
    queryKey: cb.list(filters),
    queryFn: async () => (await api.listContacts(filters)).contacts,
    enabled,
  });
}

/** Split a free-text name into first/last, falling back to the email local-part. */
export function nameParts(name: string, email: string): { firstName: string; lastName?: string } {
  const trimmed = name.trim();
  if (!trimmed) return { firstName: email.split('@')[0] || email };
  const [first, ...rest] = trimmed.split(/\s+/);
  return { firstName: first, lastName: rest.join(' ') || undefined };
}

/**
 * Whether a given email is already a CRM contact — powers the reader's
 * "In Contacts" vs "Add to contacts" state and prevents duplicate adds.
 * `api.listContacts({ search })` matches an exact email server-side; we re-check
 * client-side against every listed email so the match is address-exact.
 */
export function useContactByEmail(email: string | undefined, enabled: boolean) {
  const normalized = (email ?? '').trim().toLowerCase();
  return useQuery<ContactDetail | null>({
    queryKey: cb.byEmail(normalized),
    queryFn: async () => {
      const { contacts } = await api.listContacts({ search: normalized, status: 'active' });
      return (
        contacts.find((c) => c.emails.some((e) => e.value.trim().toLowerCase() === normalized)) ??
        null
      );
    },
    enabled: enabled && normalized.length > 0,
  });
}

/**
 * Create a CRM contact from the fuller field set collected by the
 * Add-to-Contacts dialog (name already split, plus optional company/phone/notes).
 * Distinct from {@link useCreateContact}, which is the one-tap promote path.
 */
export function useCreateDetailedContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      firstName: string;
      lastName?: string;
      email: string;
      company?: string | null;
      phone?: string | null;
      notes?: string | null;
    }) => {
      const phone = input.phone?.trim();
      return api.createContact({
        firstName: input.firstName.trim() || input.email.split('@')[0] || input.email,
        lastName: input.lastName?.trim() || undefined,
        emails: [{ label: 'email', value: input.email.trim() }],
        phones: phone ? [{ label: 'phone', value: phone }] : [],
        company: input.company?.trim() || null,
        notes: input.notes?.trim() || null,
        source: 'email',
      });
    },
    onSuccess: (_c, input) => {
      qc.invalidateQueries({ queryKey: cb.all });
      qc.invalidateQueries({ queryKey: cb.byEmail(input.email) });
    },
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      emailAddress: string;
      displayName?: string | null;
      company?: string | null;
    }) => {
      const { firstName, lastName } = nameParts(input.displayName ?? '', input.emailAddress);
      return api.createContact({
        firstName,
        lastName,
        emails: [{ label: 'email', value: input.emailAddress.trim() }],
        company: input.company ?? null,
        source: 'email',
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cb.all }),
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => api.deleteContact(contactId),
    onSuccess: () => qc.invalidateQueries({ queryKey: cb.all }),
  });
}
